import type { FC } from 'react';
import React, { useEffect } from 'react';

import { Box } from '@mui/material';
import { bindPopover, bindTrigger, usePopupState } from 'material-ui-popup-state/hooks';

import { MuiTooltip } from '@shared/ui-kit';

import { PaperActionMenuContainer, PaperActionMenuItem } from './PaperActionMenu.style';

import type { PaperActionMenuItemModel } from '@shared/models/paperActionMenuItem.model';

type PaperActionMenuProps = {
  menuList: {
    item: PaperActionMenuItemModel;
    isHideIcon?: boolean;
    callback: () => void;
    disableCallback?: () => void;
    isDisabled?: boolean;
    tooltipTitle?: string;
    itemId?: string;
  }[];
  activeItem?: string | number;
  isFullHeightContainer?: boolean;
  isSmall?: boolean;
  children?: React.ReactNode;
  onChangeMenuState?: (val: boolean) => void;
  anchorOriginVertical?: 'bottom' | 'top' | 'center';
  anchorOriginHorizontal?: 'center' | 'right' | 'left';
  transformOriginVertical?: 'bottom' | 'top' | 'center';
  transformOriginHorizontal?: 'center' | 'right' | 'left';
};

export const PaperActionMenu: FC<PaperActionMenuProps> = ({
  isSmall,
  menuList,
  activeItem,
  isFullHeightContainer,
  onChangeMenuState,
  children,
  anchorOriginHorizontal = 'right',
  anchorOriginVertical = 'bottom',
  transformOriginHorizontal = 'right',
  transformOriginVertical = 'top',
}) => {
  const popupState = usePopupState({
    variant: 'popover',
    popupId: 'demo-popup-menu',
  });

  useEffect(() => {
    if (onChangeMenuState) {
      onChangeMenuState(popupState.isOpen);
    }
  }, [popupState.isOpen, onChangeMenuState]);

  return (
    <Box sx={{ height: isFullHeightContainer ? '100%' : 'auto' }}>
      <Box
        sx={{
          display: 'inline-block',
          cursor: 'pointer',
          height: isFullHeightContainer ? '100%' : 'auto',
          width: 'auto',
        }}
        {...bindTrigger(popupState)}
      >
        {children}
      </Box>

      <PaperActionMenuContainer
        isSmall={isSmall}
        {...bindPopover(popupState)}
        anchorOrigin={{
          vertical: anchorOriginVertical,
          horizontal: anchorOriginHorizontal,
        }}
        MenuListProps={{
          'aria-labelledby': 'long-button',
        }}
        transformOrigin={{
          vertical: transformOriginVertical,
          horizontal: transformOriginHorizontal,
        }}
      >
        {menuList.map(
          (
            {
              itemId,
              isDisabled,
              disableCallback,
              callback,
              tooltipTitle,
              item: { icon: Icon, iconColor, id, label, isHideIcon, value },
            },
            i,
          ) => (
            <Box id={itemId} key={i}>
              {isDisabled ? (
                <MuiTooltip key={label} placement="left" title={tooltipTitle ?? ''}>
                  <Box
                    sx={{ marginBottom: menuList.length >= i ? '5px' : '' }}
                    onClick={() => (disableCallback ? disableCallback() : true)}
                  >
                    <PaperActionMenuItem
                      isSmall={isSmall}
                      iconColor={iconColor}
                      disabled
                      isHideIcon={isHideIcon}
                      isSetIconSize={typeof Icon === 'function'}
                    >
                      {typeof Icon === 'function' ? <Icon /> : Icon}
                      <Box component={'span'} ml={'6px'}>
                        {label}
                      </Box>
                    </PaperActionMenuItem>
                  </Box>
                </MuiTooltip>
              ) : (
                <PaperActionMenuItem
                  key={label}
                  isSmall={isSmall}
                  iconColor={iconColor}
                  isSetIconSize={typeof Icon === 'function'}
                  selected={!!(id && id === activeItem) || !!(value && value === activeItem)}
                  isHideIcon={isHideIcon}
                  onClick={(e) => {
                    popupState.close();
                    e.stopPropagation();
                    callback();
                  }}
                >
                  {typeof Icon === 'function' ? <Icon /> : Icon}
                  <Box component={'span'} ml={'6px'}>
                    {label}
                  </Box>
                </PaperActionMenuItem>
              )}
            </Box>
          ),
        )}
      </PaperActionMenuContainer>
    </Box>
  );
};
